import { ECLIPSE_OVERLAP_GLSL } from './eclipse';

export const PRIMARY_VERTEX_SHADER = `#version 300 es
precision highp float;

in float a_previousX;
in float a_previousY;
in float a_currentX;
in float a_currentY;
in float a_mass;
in float a_kind;
in float a_entityIndex;
in vec2 a_corner;

uniform float u_interpolation;
uniform float u_pointScale;
uniform float u_worldPointScale;
uniform vec2 u_cameraCenter;
uniform float u_cameraZoom;
uniform vec2 u_projectionScale;
uniform vec2 u_viewportSize;
uniform vec2 u_starPosition;
uniform vec2 u_previousPlanetPositions[16];
uniform vec2 u_currentPlanetPositions[16];
uniform float u_planetRadii[16];
uniform float u_collisionRadii[11];
uniform vec4 u_bodyMaterial[16];
uniform vec4 u_bodyClimate[16];
uniform vec4 u_bodyIceColour[16];
uniform vec4 u_bodyCloudColour[16];
uniform vec4 u_bodyLowColor[16];
uniform vec4 u_bodyHighColor[16];
uniform vec4 u_bodyLiquidColor[16];
uniform vec4 u_bodyAtmosphereColor[16];

out float v_kind;
out float v_massSignal;
out vec2 v_position;
out vec2 v_spriteCoord;
flat out float v_stellarVisibility;
flat out float v_bodyIndex;
flat out vec4 v_bodyMaterial;
flat out vec4 v_bodyClimate;
flat out vec3 v_bodyIceColour;
flat out vec3 v_bodyCloudColour;
flat out vec4 v_bodyLowColor;
flat out vec4 v_bodyHighColor;
flat out vec4 v_bodyLiquidColor;
flat out vec4 v_bodyAtmosphereColor;

${ECLIPSE_OVERLAP_GLSL}
float particleStellarVisibility(vec2 targetPosition) {
  vec2 targetFromStar = targetPosition - u_starPosition;
  float targetDistance = length(targetFromStar);
  if (targetDistance <= STAR_LIGHT_RADIUS) {
    return 1.0;
  }
  vec2 rayDirection = targetFromStar / targetDistance;
  float visibility = 1.0;
  for (int slot = 0; slot < 16; slot += 1) {
    vec2 occluderPosition = mix(
      u_previousPlanetPositions[slot],
      u_currentPlanetPositions[slot],
      u_interpolation
    );
    vec2 occluderFromStar = occluderPosition - u_starPosition;
    float alongRay = dot(occluderFromStar, rayDirection);
    if (alongRay <= STAR_LIGHT_RADIUS || alongRay >= targetDistance) {
      continue;
    }
    vec2 targetToOccluder = occluderPosition - targetPosition;
    float occluderDistance = max(0.0001, length(targetToOccluder));
    float angularSeparation = acos(clamp(
      dot(-rayDirection, targetToOccluder / occluderDistance),
      -1.0,
      1.0
    ));
    float coverage = circleOverlapFraction(
      STAR_LIGHT_RADIUS / targetDistance,
      u_planetRadii[slot] / occluderDistance,
      angularSeparation
    );
    visibility = min(visibility, 1.0 - coverage * 0.98);
  }
  return visibility;
}

void main() {
  vec2 previous = vec2(a_previousX, a_previousY);
  vec2 current = vec2(a_currentX, a_currentY);
  vec2 position = mix(previous, current, u_interpolation);
  vec2 clipPosition =
    (position - u_cameraCenter) * u_cameraZoom * u_projectionScale;
  v_kind = a_kind;
  v_position = position;
  v_spriteCoord = a_corner * 0.5 + 0.5;
  v_stellarVisibility = 1.0;
  v_massSignal = log2(max(a_mass, 0.02) + 1.0);
  v_bodyIndex = a_entityIndex;
  int bodySlot = clamp(int(a_entityIndex) - 1, 0, 15);
  int kindIndex = clamp(int(a_kind), 0, 10);
  float physicalPointSize =
    u_collisionRadii[kindIndex] * u_worldPointScale * u_cameraZoom;
  v_bodyMaterial = u_bodyMaterial[bodySlot];
  v_bodyClimate = u_bodyClimate[bodySlot];
  v_bodyIceColour = u_bodyIceColour[bodySlot].rgb;
  v_bodyCloudColour = u_bodyCloudColour[bodySlot].rgb;
  v_bodyLowColor = u_bodyLowColor[bodySlot];
  v_bodyHighColor = u_bodyHighColor[bodySlot];
  v_bodyLiquidColor = u_bodyLiquidColor[bodySlot];
  v_bodyAtmosphereColor = u_bodyAtmosphereColor[bodySlot];

  bool isOrganism =
    (a_kind >= 1.5 && a_kind < 3.5) ||
    (a_kind >= 6.5 && a_kind < 9.5);
  if (
    isOrganism &&
    (abs(clipPosition.x) > 1.08 || abs(clipPosition.y) > 1.08)
  ) {
    gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
    return;
  }
  if (isOrganism || a_kind >= 9.5) {
    v_stellarVisibility = particleStellarVisibility(position);
  }

  // Preserve distant density, but keep small life legible in close-up views.
  // This is a screen-space display minimum; collision geometry is unchanged.
  float organismPointScale = u_pointScale * mix(
    1.0, 3.0, smoothstep(0.6, 5.5, u_cameraZoom)
  );
  float spriteSize = 1.0;
  if (a_kind < 1.5) {
    // Algae are ecological state, not visible particles.
    spriteSize = 1.0;
  } else if (a_kind < 2.5) {
    spriteSize = max(2.0 * organismPointScale, physicalPointSize);
  } else if (a_kind < 3.5) {
    spriteSize = max(3.0 * organismPointScale, physicalPointSize);
  } else if (a_kind < 4.5) {
    // Reserve sprite area for a physical photosphere plus an extended corona.
    spriteSize =
      STAR_LIGHT_RADIUS * 1.6 * u_worldPointScale * u_cameraZoom;
  } else if (a_kind < 6.5) {
    // The shaded surface occupies 83% of the sprite; compensate so its disk
    // diameter matches the body's physical world-space diameter.
    spriteSize =
      (u_planetRadii[bodySlot] / 0.83) *
      u_worldPointScale *
      u_cameraZoom;
  } else if (a_kind < 7.5) {
    spriteSize = max(2.0 * organismPointScale, physicalPointSize);
  } else if (a_kind < 8.5) {
    spriteSize = max(2.0 * organismPointScale, physicalPointSize);
  } else if (a_kind < 9.5) {
    // Recovery comets are rare, large, and legible across the habitat.
    spriteSize = min(
      22.0 * u_pointScale * u_cameraZoom,
      44.0 * u_pointScale
    );
  } else {
    // Physical asteroid-belt bodies stay small but become inspectable on zoom.
    spriteSize = max(
      2.0 * u_pointScale,
      min(
        (1.2 + min(1.8, v_massSignal * 2.4)) * u_pointScale * u_cameraZoom,
        7.0 * u_pointScale
      )
    );
  }
  vec2 spriteClipOffset = a_corner * spriteSize / max(u_viewportSize, vec2(1.0));
  gl_Position = vec4(clipPosition + spriteClipOffset, 0.0, 1.0);
}`;
